import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  // Акцентът е един на екран — бутонът, който диспечерът търси в момента.
  primary:
    'kmdx-primary bg-accent text-[#0a0b0a] hover:brightness-110 active:brightness-95 disabled:bg-none disabled:bg-line-strong disabled:text-ink-faint font-semibold shadow-[0_0_18px_-6px_rgba(147,255,20,0.55)]',
  secondary:
    'bg-raised text-ink border border-line-strong hover:border-accent/60 hover:text-accent disabled:text-ink-faint',
  ghost: 'text-ink-dim hover:text-ink hover:bg-raised border border-transparent',
  danger: 'bg-danger text-white hover:brightness-110 font-semibold',
  quiet: 'text-ink-dim hover:text-accent underline underline-offset-4 decoration-line-strong',
};

const SIZES: Record<Size, string> = {
  sm: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-md',
  lg: 'h-11 px-5 text-base gap-2 rounded-lg',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', loading = false, icon, className, children, disabled, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cn(
        'inline-flex items-center justify-center whitespace-nowrap transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? <span className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" /> : icon}
      {children}
    </button>
  );
});

/** Бутон само с икона — за плътните редове на борда. */
export const IconButton = forwardRef<HTMLButtonElement, ButtonProps & { label: string }>(
  function IconButton({ label, className, children, ...rest }, ref) {
    return (
      <Button
        ref={ref}
        aria-label={label}
        title={label}
        variant="ghost"
        className={cn('px-2', className)}
        {...rest}
      >
        {children}
      </Button>
    );
  },
);
